import { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { DateCustom } from '../../../../lib/DateCustom';
import MessageCourier from '../../../../lib/MessageCourier';
import TimeTaxSupportSystemClient from '../../../../system';
import { useTranslation } from 'react-i18next';
import { getLocalizedStrings } from '../../../../lib/LocalizedStringsForDatePicker';
import { User } from '../../../../system/User';
import DataGrid from 'devextreme-react/data-grid';
import { IReporteGralHoras } from '../../../../system/reporting/ReportingSystem';
import { crearSelectorColores } from '../../../../lib/crearSelectorColores';
import { IorganizationLevels } from '../../../../system/AuthenticationSystem';
import { Item, TabPanel } from 'devextreme-react/tab-panel';
import useIsMobile from '../../../../common/custom-hooks/useIsMobile';
import CumplimientoCargaItem from './CumplimientoCargaItem';
import InicioCargaHorasItem from './InicioCargaHorasItem';
import { DatePicker, Icon, PrimaryButton, Text } from '@fluentui/react';

interface IAvancePorSectorProps {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (change: boolean) => void;
  user: User;
}

type ChartDataItem = {
  name: string;
  value: number;
  color: string;
};

const datetime = DateTime.now();
const today = datetime.toJSDate();
const firstDayOfMonth = DateTime.fromJSDate(today).startOf('month').toJSDate();
const lastDayOfMonth = DateTime.fromJSDate(today).endOf('month').toJSDate();

const AvancePorSector = (props: IAvancePorSectorProps) => {
  const { t } = useTranslation();

  const [fromDate, setFromDate] = useState<DateTime>(DateTime.fromJSDate(firstDayOfMonth));
  const [toDate, setToDate] = useState<DateTime>(DateTime.fromJSDate(lastDayOfMonth));
  const [items, setItems] = useState<IReporteGralHoras[]>([]);
  const [chartData1, setChartData1] = useState<ChartDataItem[]>([]);
  const [chartData2, setChartData2] = useState<ChartDataItem[]>([]);
  const [levelsN, setLevelsN] = useState<IorganizationLevels[]>([]);

  const chartRef1 = useRef<HTMLDivElement>(null);
  const chartRef2 = useRef<HTMLDivElement>(null);
  const gridRef = useRef<DataGrid>(null);

  /* ============================
       CARGA NIVELES
    ============================ */
  useEffect(() => {
    const loadLevels = async () => {
      const data = await props.system.getOrganizationLevels();
      setLevelsN(data);
    };
    loadLevels();
  }, []);

  const buildJerarquia = (data: any[]) => {
    const tree: any = {};

    const ultimoNivelMaster = levelsN[levelsN.length - 1];
    const nivelPadreMaster = levelsN[0];

    data.forEach((item) => {
      const path = item.organigrama_path_nombre;
      const esUltimoNivel = item.ultimo_nivel;

      const ultimoNivel = item[ultimoNivelMaster.nio_nombre_tec];

      let current = tree;

      // Agregar primero el nodo padre del item
      const nodoPadre = item[nivelPadreMaster.nio_nombre_tec] || t('SIN ESTRUCTURA');
      if (nodoPadre === 'SIN ESTRUCTURA' || nodoPadre === t('SIN ESTRUCTURA')) {
        return;
      }

      if (!current[nodoPadre]) {
        current[nodoPadre] = {
          nombre: nodoPadre,
          hijos: {},
          items: [],
        };
      }

      // Luego agregar el nodo del último nivel, pero solo si es necesario para mantener la estructura aunque no tenga items asignados
      if ((esUltimoNivel || path) && ultimoNivel) {
        if (!current[nodoPadre].hijos[ultimoNivel]) {
          current[nodoPadre].hijos[ultimoNivel] = {
            nombre: ultimoNivel,
            hijos: {},
            items: [],
          };
        }
      }

      // Agregar el item al nodo del último nivel si es que es el último nivel o si no tiene path pero tiene un nivel identificable
      if (esUltimoNivel) {
        current[nodoPadre].hijos[ultimoNivel].items.push(item);
      } else if (!esUltimoNivel) {
        // Si el item no es del último nivel, pero tampoco tiene path, lo asigno a un nodo genérico dentro de su nodo padre para no perderlo y mostrar que no completó la estructura
        const key = 'no_completo_estructura';
        if (!current[nodoPadre].hijos[key]) {
          current[nodoPadre].hijos[key] = {
            nombre: `${t('Area vacía')} (${nodoPadre.toLowerCase()})`,
            hijos: {},
            items: [],
          };
        }
        current[nodoPadre].hijos[key].items.push(item);
      }
    });

    return tree;
  };

  const flattenTree = (node: any, nivel = 0, parent = null, root = null) => {
    let result: any[] = [];

    Object.values(node).forEach((nodo: any) => {
      // si estoy en nivel 0, yo soy el root
      const currentRoot = nivel === 0 ? nodo.nombre : root;

      // hijos primero
      result = result.concat(flattenTree(nodo.hijos, nivel + 1, nodo.nombre, currentRoot));

      // cálculos
      const colaboradores = nodo.items.length;

      // sumo horas de todos los colaboradores de ese nodo
      const horas = nodo.items.reduce((acc: number, i: any) => acc + Number(i.horas || 0), 0);

      // colaboradores que completaron su carga horaria esperada
      const completaron = nodo.items.filter((i: any) => i.porcentaje_carga >= 100).length;

      // colaboradores que no completaron su carga horaria esperada
      const noCompletaron = colaboradores - completaron;

      // Completaron / Total de colaboradores del nodo, pero solo si el nodo tiene colaboradores, si no, el porcentaje es 0 para evitar división por 0
      const porcentaje = colaboradores > 0 ? Math.ceil((completaron / colaboradores) * 10000) / 100 : 0; // redondeo a 2 decimales

      result.push({
        nombre: nodo.nombre,
        colaboradores,
        horas,
        completaron,
        noCompletaron,
        porcentaje,
        nivel,
        parent,
        isParent: nivel === 0,
        grupo: currentRoot,
      });
    });

    return result;
  };

  const buildJerarquiaDinamica = (data: any[]) => {
    const tree = buildJerarquia(data);
    return flattenTree(tree);
  };

  const resumenDataGrid = buildJerarquiaDinamica(items).filter((x) => !x.isParent);
  const { isMobile } = useIsMobile();

  const onChangeDate = (date: Date | null | undefined, name: string) => {
    if (date) {
      if (name === 'from') {
        // Primer dia del mes seleccionado
        const firstDay = DateTime.fromJSDate(date).startOf('month');
        setFromDate(firstDay);

        // Ultimo dia del mes seleccionado
        const lastDay = DateTime.fromJSDate(date).endOf('month');
        setToDate(lastDay);
      }
    }
  };

  const handleSearch = async () => {
    props.changeBlockUI(true);
    try {
      if (fromDate && toDate) {
        const _items = await props.system.getReportingSystem().getGralHorasReport(fromDate, toDate);
        setItems(_items);

        const resumen = buildJerarquiaDinamica(_items);

        const padres = resumen.filter((x) => x.isParent);
        const hijos = resumen.filter((x) => !x.isParent);

        const selectorPadres = crearSelectorColores(padres.length);
        const selectorHijos = crearSelectorColores(hijos.length);

        const chartData1: ChartDataItem[] = padres.map((padre) => {
          const hijosDelPadre = hijos.filter((hijo) => hijo.grupo === padre.nombre);

          // promedio de sus procentajes
          const porcentaje =
            Math.ceil(((hijosDelPadre.reduce((acc, hijo) => acc + hijo.porcentaje, 0) + padre.porcentaje) / hijosDelPadre.length) * 100) / 100;

          return {
            name: padre.nombre,
            value: porcentaje,
            color: selectorPadres.obtenerColor(),
          };
        });

        const chartData2: ChartDataItem[] = hijos.map((item) => {
          return {
            name: item.nombre,
            value: item.porcentaje,
            color: selectorHijos.obtenerColor(),
          };
        });

        setChartData1(chartData1);
        setChartData2(chartData2);
        props.courier.messageSuccess(t('Reporte generado con éxito'));
      } else {
        props.courier.messageError(t('Debes seleccionar un sector'));
      }
    } catch (error) {
      props.courier.messageError(error);
    }
    props.changeBlockUI(false);
  };

  return (
    <div>
      <div className="ms-Grid-row mt-1">
        <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg3">
          <DatePicker
            strings={getLocalizedStrings()}
            label={t("Periodo")}
            formatDate={DateCustom.formatDateMMYYYY}
            value={fromDate ? fromDate.toJSDate() : undefined}
            onSelectDate={(e) => onChangeDate(e, "from")}
            calendarProps={{
              showGoToToday: false,
              isDayPickerVisible: false,
            }}
          />
        </div>
        <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg3">
          <PrimaryButton
            iconProps={{ iconName: "Search" }}
            className="mt-for-btn-search ml-2 avance-sector-search-btn"
            onClick={() => handleSearch()}
            text={t("Buscar")}
          />
        </div>
      </div>
      <hr />

      {resumenDataGrid.length > 0 ? (
        <>
          <div style={{ width: '100%', overflow: 'auto' }}>
            <TabPanel
              id="tabPanel"
              deferRendering={false}
              className={`${isMobile ? "container-mobile-title-table" : ""}`}
            >
              <Item title={t("Cumplimiento del 100% de carga")}>
                <CumplimientoCargaItem
                  user={props.user}
                  system={props.system}
                  courier={props.courier}
                  changeBlockUI={props.changeBlockUI}
                  items={items}
                  levelsN={levelsN}
                  fromDate={fromDate}
                />
              </Item>
              <Item title={t("Inicio de carga de horas")}>
                <InicioCargaHorasItem
                  user={props.user}
                  system={props.system}
                  courier={props.courier}
                  changeBlockUI={props.changeBlockUI}
                  items={items}
                  levelsN={levelsN}
                  fromDate={fromDate}
                />
              </Item>
            </TabPanel>
          </div>
        </>
      ) : (
        <div className="empty-state-panel">
          <Icon iconName="BarChart4" className="empty-state-panel-icon"></Icon>
          <Text variant="medium" className="empty-state-panel-text">
            {t('No hay información de avance por sector para el periodo seleccionado')}
          </Text>
        </div>
      )}
    </div>
  );
};

export default AvancePorSector;
